/**
 * Top-level error boundary.
 *
 * Without this, any uncaught render error in any page or component
 * crashes the entire React tree to a blank white screen. A single
 * malformed history item, broken comment data shape, or null-deref
 * in a tool component takes the whole dashboard down — the teacher
 * sees no error, no recovery, just a blank page that doesn't even
 * have a "go back" button.
 *
 * Class component because React still requires `componentDidCatch`
 * + `getDerivedStateFromError` to live on a class, even with hooks
 * everywhere else. The TanStack Query / Sentry ecosystems have hook-
 * based wrappers but they all forward to a class boundary under the
 * hood.
 *
 * Wraps the entire <AppRoutes /> tree in App.tsx. A per-route
 * boundary would be nicer (so a crash on /dashboard/rubric doesn't
 * blank the navigation chrome) but adds noise; this is the minimum
 * useful defense, and the recovery action ("Refresh page") is
 * already coarse enough that per-route doesn't buy much.
 */

import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface to the browser console — production logging would
    // hook in here too (Sentry / Datadog / similar). The
    // componentStack is invaluable for diagnosing which subtree
    // crashed.
    console.error("[ErrorBoundary] Uncaught render error:", error);
    console.error("[ErrorBoundary] Component stack:", info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    // Plain inline styles so a CSS-loading failure doesn't make the
    // recovery UI ALSO unrenderable. We're already in a degraded
    // state; assume nothing about the rest of the app working.
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          fontFamily:
            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          background: "#fafaf9",
          color: "#1c1917",
        }}
      >
        <div
          style={{
            maxWidth: "32rem",
            width: "100%",
            background: "#fff",
            border: "1px solid #e7e5e4",
            borderRadius: "0.75rem",
            padding: "2rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <h1 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "0.5rem" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#57534e", marginBottom: "1rem", fontSize: "0.875rem" }}>
            The page hit an unexpected error. Reloading usually fixes it.
            If it keeps happening, please email{" "}
            <a
              href="mailto:hi@teacherdashboard.ai"
              style={{ color: "#7c2d12", textDecoration: "underline" }}
            >
              hi@teacherdashboard.ai
            </a>{" "}
            with what you were doing.
          </p>
          {/* Show the error message in a small dev-friendly box.
              Helps support requests + curious teachers. */}
          {this.state.error.message && (
            <pre
              style={{
                background: "#f5f5f4",
                border: "1px solid #e7e5e4",
                borderRadius: "0.375rem",
                padding: "0.5rem 0.75rem",
                fontSize: "0.75rem",
                color: "#44403c",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginBottom: "1.25rem",
                maxHeight: "8rem",
                overflowY: "auto",
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                padding: "0.625rem 1rem",
                borderRadius: "0.5rem",
                background: "#7c2d12",
                color: "#fff",
                border: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                padding: "0.625rem 1rem",
                borderRadius: "0.5rem",
                background: "#fff",
                color: "#44403c",
                border: "1px solid #d6d3d1",
                fontWeight: 500,
                fontSize: "0.875rem",
                cursor: "pointer",
              }}
            >
              Try again without reloading
            </button>
          </div>
        </div>
      </div>
    );
  }
}
